import * as React from "react";
import URI from "urijs";
import Routes from "routing/Routes";
import { Button } from "components/bootstrap";
import { LinkContainer, ReadOnlyFormGroup, Spinner } from "components/common";
import SectionComponent from "components/common/Section/SectionComponent";
import useRoles from "common/hooks/useRoles";
import useSAMLBackendDetails from "authentication/components/saml/hooks/useSAMLBackendDetails";
import SAMLCertInfoTable from "authentication/components/saml/config/SAMLCertInfoTable";
import { SSO_SAML_ACS_PATH, SSO_SAML_LOGOUT_PATH } from "authentication/components/saml/constants";
const normalizeURI = (url) => new URI(url).normalizePath().toString();
const ConfigValue = ({ label, value, code = false }) => value && /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label, value: code ? /* @__PURE__ */ React.createElement("code", null, value) : value });
const certDetails = (isLoading, certs) => {
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading certificate details..." });
  }
  if (!certs) {
    return /* @__PURE__ */ React.createElement("em", null, "Couldn't load certificate details!");
  }
  return Object.keys(certs || {}).map((key) => {
    const { certificate, subject, issuer, sha1_fingerprint, sha256_fingerprint, valid_from, valid_to } = certs[key];
    return /* @__PURE__ */ React.createElement(
      SAMLCertInfoTable,
      {
        key: sha256_fingerprint,
        certificate,
        subject,
        issuer,
        sha256Fingerprint: sha256_fingerprint,
        sha1Fingerprint: sha1_fingerprint,
        validFrom: valid_from,
        validTo: valid_to
      }
    );
  });
};
const SAMLBackendConfigSection = ({ authenticationBackend }) => {
  const { isLoading, roles } = useRoles();
  const {
    id,
    title,
    description,
    defaultRoles,
    defaultUserTimezone,
    idpMetadataUrl,
    idpMetadataRefreshAtLogin,
    idpSsoServicePostBindingUrl,
    idpSsoServiceRedirectBindingUrl,
    idpLogoutServicePostBindingUrl,
    idpLogoutServiceRedirectBindingUrl,
    idpEntityId,
    idpUserNameAttribute,
    idpEmailAttribute,
    idpFirstNameAttribute,
    idpLastNameAttribute,
    idpFullNameAttribute,
    spBaseUrl,
    spEntityId
  } = authenticationBackend;
  const { isLoading: areDetailsLoading, details } = useSAMLBackendDetails(id);
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const foundRoles = defaultRoles.split(",").map((roleId) => {
    var _a;
    return (_a = roles.find((role) => role.id === roleId)) == null ? void 0 : _a.name;
  }).join(", ");
  const idpCertificates = certDetails(areDetailsLoading, details == null ? void 0 : details.idp_certs);
  const spCertificates = certDetails(areDetailsLoading, details == null ? void 0 : details.sp_certs);
  return /* @__PURE__ */ React.createElement(
    SectionComponent,
    {
      title: "Server Configuration",
      headerActions: /* @__PURE__ */ React.createElement(LinkContainer, { to: Routes.SYSTEM.AUTHENTICATION.BACKENDS.edit(id, "idp_config") }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", bsSize: "small" }, "Edit"))
    },
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Title", value: title }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Description", value: description }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Default roles", value: foundRoles }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Default user time zone", value: defaultUserTimezone || "Browser's time zone" }),
    /* @__PURE__ */ React.createElement("hr", null),
    /* @__PURE__ */ React.createElement("h3", null, "Identity Provider"),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Metadata URL", value: idpMetadataUrl }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Metadata Refresh At Login", value: idpMetadataRefreshAtLogin }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "SSO Service Redirect URL", value: idpSsoServiceRedirectBindingUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "SSO Service POST URL", value: idpSsoServicePostBindingUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Logout Service Redirect URL", value: idpLogoutServiceRedirectBindingUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Logout Service POST URL", value: idpLogoutServicePostBindingUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Entity ID", value: idpEntityId }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Certificate", value: idpCertificates }),
    /* @__PURE__ */ React.createElement("hr", null),
    /* @__PURE__ */ React.createElement("h3", null, "Service Provider"),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Base URL", value: spBaseUrl }),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Entity ID", value: spEntityId }),
    /* @__PURE__ */ React.createElement(
      ConfigValue,
      {
        label: "Assertion Consumer Service (ACS) URL",
        value: normalizeURI(`${spBaseUrl}/${SSO_SAML_ACS_PATH}`)
      }
    ),
    /* @__PURE__ */ React.createElement(ConfigValue, { label: "Single Logout Service URL", value: normalizeURI(`${spBaseUrl}/${SSO_SAML_LOGOUT_PATH}`) }),
    /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Certificate", value: spCertificates }),
    [
      idpUserNameAttribute,
      idpEmailAttribute,
      idpFirstNameAttribute,
      idpLastNameAttribute,
      idpFullNameAttribute
    ].some((value) => !!value) && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("hr", null), /* @__PURE__ */ React.createElement("h3", null, "User Attribute Mapping"), /* @__PURE__ */ React.createElement(ConfigValue, { label: "User Name", value: idpUserNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: "Email", value: idpEmailAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: "First Name", value: idpFirstNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: "Last Name", value: idpLastNameAttribute }), /* @__PURE__ */ React.createElement(ConfigValue, { label: "Full Name", value: idpFullNameAttribute }))
  );
};
export default SAMLBackendConfigSection;
